import * as ToggleGroup from '@radix-ui/react-toggle-group';

/**
 * Shared "raised segment" recipe for single-select toggle groups: the active
 * item lifts to surface-4 (one elevation above any surface-2 track) plus a
 * shadow, so it reads clearly in dark mode instead of blending into the track.
 * Reused by the sidebar ThemeToggle — keep both in sync via this constant.
 */
export const toggleActiveClasses = 'data-[state=on]:bg-surface-4 data-[state=on]:text-fg data-[state=on]:shadow-sm';

interface SegmentedControlOption<T extends string> {
  value: T;
  label: React.ReactNode;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedControlOption<T>[];
  ariaLabel?: string;
}

export function SegmentedControl<T extends string>({ value, onChange, options, ariaLabel }: SegmentedControlProps<T>) {
  return (
    <ToggleGroup.Root
      type="single"
      value={value}
      onValueChange={(next) => {
        if (next) onChange(next as T);
      }}
      aria-label={ariaLabel}
      className="inline-flex items-center gap-0.5 rounded-lg border border-line bg-surface-2 p-0.5"
    >
      {options.map((opt) => (
        <ToggleGroup.Item
          key={opt.value}
          value={opt.value}
          className={`cursor-pointer select-none rounded-md px-3 py-1 text-[12.5px] font-medium text-fg-tertiary outline-none transition-colors hover:text-fg focus-visible:ring-2 focus-visible:ring-ring/40 ${toggleActiveClasses}`}
        >
          {opt.label}
        </ToggleGroup.Item>
      ))}
    </ToggleGroup.Root>
  );
}
